import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, CheckCircle, AlertCircle, Mail, MessageSquare, DollarSign } from 'lucide-react';

type InquiryType = 'collaboration' | 'media' | 'business' | 'other';

const inquiryTypes: { value: InquiryType; label: string; icon: React.ReactNode }[] = [
  { value: 'collaboration', label: 'Brand Collaboration', icon: <MessageSquare size={16} /> },
  { value: 'media', label: 'Media / Interview', icon: <Send size={16} /> },
  { value: 'business', label: 'Business Inquiry', icon: <DollarSign size={16} /> },
  { value: 'other', label: 'Other', icon: <Mail size={16} /> },
];

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    inquiryType: '' as InquiryType | '',
    budget: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    try {
      setStatus('loading');
      setErrorMsg('');

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/contact-form`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            inquiry_type: form.inquiryType,
            budget: form.budget,
            message: form.message,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Failed to send message');

      setStatus('success');
      setForm({ name: '', email: '', inquiryType: '', budget: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  const isValid = form.name && form.email && form.message;

  return (
    <div className="bg-black min-h-screen">
      {/* Hero */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-400/5 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl" />
        
        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-sm font-medium mb-6">
              <Mail size={14} />
              Get in touch
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Let's{' '}
              <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
                Work Together
              </span>
            </h1>
            <p className="text-zinc-400 text-lg">
              Whether it's a sponsorship, partnership, or media inquiry — we read every message and respond within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24 px-6">
        <div className="container-custom">
          <div className="max-w-xl mx-auto">
            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-12 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-green-400/10 border border-green-400/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={32} className="text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">Message Sent!</h2>
                <p className="text-zinc-400">
                  We'll get back to you within 24 hours. Keep an eye on your inbox.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-yellow-400 hover:text-yellow-300 text-sm font-medium"
                >
                  Send another message →
                </button>
              </motion.div>
            ) : (
              <motion.form
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 space-y-6"
              >
                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-yellow-400/50 focus:ring-1 focus:ring-yellow-400/20 transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-yellow-400/50 focus:ring-1 focus:ring-yellow-400/20 transition-colors text-sm"
                    />
                  </div>
                </div>

                {/* Inquiry Type */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    What are you reaching out about?
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {inquiryTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, inquiryType: type.value }))}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-medium transition-all ${
                          form.inquiryType === type.value
                            ? 'bg-yellow-400/10 border-yellow-400/50 text-yellow-400'
                            : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-300'
                        }`}
                      >
                        <span className="flex-shrink-0">{type.icon}</span>
                        <span className="text-left leading-tight">{type.label.split(' / ')[0]}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Budget (optional) */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Budget range <span className="text-zinc-600">(optional)</span>
                  </label>
                  <select
                    name="budget"
                    value={form.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-400 focus:outline-none focus:border-yellow-400/50 focus:ring-1 focus:ring-yellow-400/20 transition-colors text-sm appearance-none cursor-pointer"
                  >
                    <option value="">Prefer not to say</option>
                    <option value="under_25k">Under ₹25,000</option>
                    <option value="25k_50k">₹25,000 – ₹50,000</option>
                    <option value="50k_1l">₹50,000 – ₹1,00,000</option>
                    <option value="1l_5l">₹1,00,000 – ₹5,00,000</option>
                    <option value="5l_plus">₹5,00,000+</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project, what you're looking for, and any relevant details..."
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-yellow-400/50 focus:ring-1 focus:ring-yellow-400/20 transition-colors text-sm resize-none"
                  />
                </div>

                {/* Cloudflare Turnstile */}
                <div className="cf-turnstile" data-sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'} data-theme="dark" />

                {/* Error */}
                {status === 'error' && (
                  <div className="flex items-center gap-2 p-4 rounded-xl bg-red-400/10 border border-red-400/20 text-red-400 text-sm">
                    <AlertCircle size={16} />
                    {errorMsg}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!isValid || status === 'loading'}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-yellow-400 hover:bg-yellow-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-semibold text-sm transition-colors cursor-pointer"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={16} />
                    </>
                  )}
                </button>

                <p className="text-center text-zinc-600 text-xs">
                  We typically respond within 24 hours. No spam, ever.
                </p>
              </motion.form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
